import type { ReactNode } from 'react';
import type { Capability, MeResponse, StaffRole } from '@ucp/web/lib';
import { hasAnyRole, type BaselineCapability } from './navConfig';
import { AccessDeniedView } from '../access/AccessDeniedView';

/**
 * Per-route authorisation (PRO-0250, D5). A configured user who reaches a route they
 * lack the role for is shown an in-shell "you don't have access" state
 * (`AccessDeniedView`) — with the surface still highlighted in the rail and, where
 * the capability is self-requestable, an ADR-036 "Request access" affordance —
 * rather than being silently redirected to Comms Home (the previous behaviour, which
 * looked like the app "defaulting to Communications"). This is UX / defence-in-depth
 * only — the server remains authoritative for every data call (capability + RLS,
 * ADR-009/010); the guard must never be the load-bearing control (SF-SEC-001).
 */
export interface RouteGuardProps {
  user: Pick<MeResponse, 'roles'> &
    Partial<Pick<MeResponse, 'featureFlags' | 'workforceBaseline'>>;
  anyOf: StaffRole[];
  baselineCapability?: BaselineCapability;
  /** Human name of the guarded surface, shown in the deny state (e.g. "People Assistant"). */
  label?: string;
  /**
   * The capability the surface needs. When it maps to a self-requestable role
   * (`REQUESTABLE_ROLES`) the deny state offers the in-app request; otherwise it
   * shows the service-desk contact fallback.
   */
  requestCapability?: Capability;
  children: ReactNode;
}

export function RouteGuard({
  user,
  anyOf,
  baselineCapability,
  label,
  requestCapability,
  children,
}: RouteGuardProps) {
  if (!hasAnyRole(user, anyOf, undefined, baselineCapability)) {
    return (
      <AccessDeniedView
        {...(label !== undefined ? { surfaceLabel: label } : {})}
        accessRequestEnabled={user.featureFlags?.accessRequestEnabled ?? false}
        {...(requestCapability !== undefined ? { requestCapability } : {})}
      />
    );
  }
  return <>{children}</>;
}
